import { cn } from "@/utils/helpers"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"
import * as React from "react"
import { Icon } from "./icon/Icon"
import { TIconName } from "./icon/IconUtil"
import { useTranslation } from "react-i18next"
import { Spinner } from "./Spinner"
import { Tooltip } from "./Tooltip"

const buttonVariants = cva(
  "group/button inline-flex items-center justify-center outline-transparent whitespace-nowrap gap-2 outline-1 enabled:cursor-pointer rounded-full text-md-medium transition-[color,_background-color,_border-color,_text-decoration-color,_fill,_stroke,_box-shadow,_opacity] duration-200 ease-out outline-offset-1 enabled:hover:shadow-elevation-1 active:shadow-none",
  {
    variants: {
      variant: {
        default:
          "text-on-primary bg-primary enabled:hover:shadow-elevation-1 disabled:bg-on-surface/20 disabled:text-on-surface/50 focus:outline-primary",
        tonal:
          "bg-secondary-container text-on-secondary-container enabled:hover:shadow-elevation-1 disabled:bg-on-surface/20 disabled:text-on-surface/50 focus:outline-primary",
        outline:
          "border border-secondary text-primary enabled:hover:bg-primary/10 enabled:focus:bg-primary/10 enabled:focus:border-primary enabled:active:bg-primary/10 disabled:border-on-surface/20 disabled:text-on-surface/50 enabled:hover:shadow-none",
        text: "border border-transparent text-primary enabled:hover:bg-primary/10 enabled:focus:bg-primary/10 enabled:focus:border-primary/20 enabled:active:bg-primary/10 enabled:active:border-primary disabled:text-on-surface/50 enabled:hover:shadow-none",
        destructive:
          "text-on-primary bg-error enabled:hover:bg-error/95 enabled:focus:bg-error/95 enabled:active:bg-error/95",
        "destructive-tonal":
          "text-on-error-container bg-error-container enabled:hover:bg-[#F3C8C7] enabled:focus:bg-[#F1C0BE] enabled:active:bg-[#F1C0BE] disabled:bg-on-surface/20 disabled:text-on-surface/50",
        "destructive-outline":
          "border border-error text-error enabled:hover:bg-error/8 enabled:focus:bg-error/12 enabled:active:bg-error/12  disabled:border-on-surface/20 disabled:text-on-surface/50 enabled:hover:shadow-none",
        "destructive-text":
          "border border-transparent text-error enabled:hover:bg-error/8 enabled:focus:bg-error/12 enabled:active:bg-error/12 disabled:text-on-surface/50 enabled:hover:shadow-none",
      },
      size: {
        default: "px-6 py-3 h-10 min-w-14",
        icon: "h-10 w-10 text-on-surface-variant enabled:hover:bg-on-surface-variant/10 enabled:focus:bg-on-surface-variant/15 enabled:active:bg-on-surface-variant/20 flex justify-center items-center",
      },
    },
    compoundVariants: [
      {
        size: ["icon"],
        variant: ["outline", "text"],
        className:
          "text-on-surface-variant enabled:hover:bg-on-surface/8 enabled:focus:bg-on-surface/12 enabled:active:bg-on-surface/12",
      },
      {
        size: ["icon"],
        variant: ["text"],
        className:
          "enabled:focus:border-transparent enabled:active:border-transparent",
      },
      {
        size: ["icon"],
        variant: ["outline"],
        className:
          "enabled:focus:border-secondary enabled:active:border-secondary  border-outline",
      },
      {
        size: ["icon"],
        variant: ["default"],
        className: "border-0",
      },
    ],
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
)

export type ButtonProps = React.ButtonHTMLAttributes<HTMLButtonElement> &
  Omit<VariantProps<typeof buttonVariants>, "size"> & {
    asChild?: boolean
    iconName?: TIconName
    iconClassName?: string
    rightIconName?: TIconName
    loading?: boolean
    loadingRight?: boolean
  } & (
    | {
        size: "icon"
        text: React.ReactNode
      }
    | {
        size?: "default"
        text?: React.ReactNode
      }
  )

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant,
      size,
      asChild = false,
      children,
      iconName,
      iconClassName,
      rightIconName,
      text,
      loading,
      loadingRight = false,
      ...props
    },
    ref,
  ) => {
    const Comp = asChild ? Slot : "button"
    const { t } = useTranslation("common")
    const { disabled, ...restProps } = props

    const ButtonComp = (
      <Comp
        className={cn(buttonVariants({ variant, size }), className)}
        ref={ref}
        aria-label={restProps["aria-label"] || "Button"}
        {...restProps}
        disabled={Boolean(loading || disabled)}
      >
        {asChild ? (
          children
        ) : (
          <>
            {!loadingRight && loading ? (
              <Spinner className={cn("text-on-surface-variant")} />
            ) : (
              iconName && (
                <Icon
                  icon={iconName}
                  size={size === "icon" ? "1.5rem" : "1.25rem"}
                  className={cn({ "-ml-2": size !== "icon" }, iconClassName)}
                />
              )
            )}
            {children}
            {loadingRight && loading ? (
              <Spinner className={cn("text-on-surface-variant")} />
            ) : (
              rightIconName && (
                <Icon
                  icon={rightIconName}
                  size={size === "icon" ? "1.5rem" : "1.25rem"}
                  className={cn({ "-mr-2": size !== "icon" })}
                />
              )
            )}
          </>
        )}
      </Comp>
    )

    return text ? (
      <Tooltip content={typeof text === "string" ? t(text) : text} asChild>
        {ButtonComp}
      </Tooltip>
    ) : (
      ButtonComp
    )
  },
)
Button.displayName = "Button"

export { Button, buttonVariants }
